import * as z from 'zod';

export const GetTimeSlotsRequest = z.object({
  reservationLocationId: z
    .string()
    .describe(
      'ID of the reservation location for which to retrieve time slots.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  date: z
    .date()
    .describe(
      'Date and time for which to retrieve a time slot in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format.'
    ),
  partySize: z
    .number()
    .int()
    .describe(
      'Size of the party that needs to be seated during this time slot.\n\nMin: `1`'
    )
    .min(1)
    .max(10000),
  options: z
    .object({
      duration: z
        .number()
        .int()
        .describe('Duration in minutes of the time slot.\n\nMin: `5`')
        .min(5)
        .max(1000)
        .optional()
        .nullable(),
      slotsBefore: z
        .number()
        .int()
        .describe(
          'The number of time slots to retrieve before the specified `date`.'
        )
        .min(0)
        .max(50)
        .optional()
        .nullable(),
      slotsAfter: z
        .number()
        .int()
        .describe(
          'The number of time slots to retrieve after the specified `date`.'
        )
        .min(0)
        .max(50)
        .optional()
        .nullable(),
    })
    .describe('Options for retrieving the time slots.')
    .optional(),
});
export const GetTimeSlotsResponse = z.object({
  timeSlots: z
    .array(
      z.object({
        startDate: z
          .date()
          .describe('Start date and time of this time slot.')
          .optional()
          .nullable(),
        duration: z
          .number()
          .int()
          .describe('Duration in minutes of this time slot.')
          .optional(),
        status: z
          .enum(['AVAILABLE', 'UNAVAILABLE', 'NON_WORKING_HOURS'])
          .describe('Availability status of this time slot.')
          .optional(),
        manualApproval: z
          .boolean()
          .describe(
            'Whether manual approval is required to make a reservation in this time slot.'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const CheckTimeSlotRequest = z.object({
  reservationLocationId: z
    .string()
    .describe(
      'ID of the reservation location for which to check the time slot.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    date: z.date().describe('Date and time of the time slot to check.'),
    duration: z
      .number()
      .int()
      .describe('Duration of the time slot in minutes .\n\nMin: `5`')
      .min(5)
      .max(1000),
    partySize: z
      .number()
      .int()
      .describe("Party size to check the restaurant's availability for.")
      .min(1)
      .max(10000),
    excludeReservationId: z
      .string()
      .describe(
        'ID of a reservation to ignore during the check.\n\nUse this when rescheduling a reservation to exclude it in its current state from the availability calculations.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
  }),
});
export const CheckTimeSlotResponse = z.object({
  tableCombinationAvailabilities: z
    .array(
      z.object({
        tableIds: z.array(z.string()).max(2000).optional(),
        tableCombinationConflicts: z
          .array(
            z.enum([
              'UNKNOWN',
              'RESERVED',
              'TOO_BIG',
              'TOO_SMALL',
              'OFFLINE_ONLY',
              'RESERVED_FOR_EXPERIENCE',
              'TABLE_NOT_IN_AREA',
            ])
          )
          .max(2)
          .optional(),
      })
    )
    .optional(),
  reservationLocationConflicts: z
    .array(z.enum(['UNKNOWN', 'PARTY_PACING', 'SEAT_PACING']))
    .optional(),
  timeSlotAvailability: z
    .intersection(
      z.object({
        timeSlotAvailabilityType: z
          .enum(['STANDARD', 'EXPERIENCE'])
          .describe('Type of availability.')
          .optional(),
      }),
      z.xor([
        z.object({
          standardOptions: z.never().optional(),
          experienceOptions: z.never().optional(),
        }),
        z.object({
          experienceOptions: z.never().optional(),
          standardOptions: z
            .object({
              tableCombinationAvailabilities: z
                .array(
                  z.object({
                    tableIds: z.array(z.string()).max(2000).optional(),
                    tableCombinationConflicts: z
                      .array(
                        z.enum([
                          'UNKNOWN',
                          'RESERVED',
                          'TOO_BIG',
                          'TOO_SMALL',
                          'OFFLINE_ONLY',
                          'RESERVED_FOR_EXPERIENCE',
                          'TABLE_NOT_IN_AREA',
                        ])
                      )
                      .max(2)
                      .optional(),
                  })
                )
                .max(2000)
                .optional(),
              reservationLocationConflicts: z
                .array(z.enum(['UNKNOWN', 'PARTY_PACING', 'SEAT_PACING']))
                .max(3)
                .optional(),
              experienceBlockingConflicts: z
                .array(z.enum(['UNKNOWN', 'BLOCKED_BY_EXPERIENCE']))
                .max(3)
                .optional(),
            })
            .describe('Response for standard reservation.'),
        }),
        z.object({
          standardOptions: z.never().optional(),
          experienceOptions: z
            .object({
              tableCombinationAvailabilities: z
                .array(
                  z.object({
                    tableIds: z.array(z.string()).max(2000).optional(),
                    experienceTableCombinationConflicts: z
                      .array(
                        z.enum([
                          'UNKNOWN',
                          'RESERVED',
                          'TOO_BIG',
                          'TOO_SMALL',
                          'OUTSIDE_EXPERIENCE_TABLE_LIST',
                          'TABLE_NOT_IN_AREA',
                        ])
                      )
                      .max(2)
                      .optional(),
                  })
                )
                .max(2000)
                .optional(),
              experienceConflicts: z
                .array(z.enum(['UNKNOWN', 'MAXIMUM_NUMBER_OF_GUESTS']))
                .max(5)
                .optional(),
            })
            .describe('Response for a reservation that contains experience.'),
        }),
      ])
    )
    .describe('Time slot availability')
    .optional(),
});
export const GetScheduledTimeSlotsRequest = z.object({
  reservationLocationId: z
    .string()
    .describe(
      'ID of the reservation location for which to retrieve time slots.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  partySize: z
    .number()
    .int()
    .describe(
      'Size of the party that needs to be seated during this time slot.\n\nMin: `1`'
    )
    .min(1)
    .max(10000),
  options: z.object({
    timeRange: z
      .object({
        startDate: z
          .date()
          .describe(
            'Date and time from which to retrieve time slots in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format.'
          ),
        endDate: z
          .date()
          .describe(
            'Date and time to which to retrieve time slots in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601#coordinated_Universal_Time_(UTC)) format.'
          ),
      })
      .describe('Time range from which to retrieve time slots.'),
  }),
});
export const GetScheduledTimeSlotsResponse = z.object({
  timeSlots: z
    .array(
      z.object({
        startDate: z
          .date()
          .describe('Start date and time of this time slot.')
          .optional()
          .nullable(),
        duration: z
          .number()
          .int()
          .describe('Duration in minutes of this time slot.')
          .optional(),
        status: z
          .enum(['AVAILABLE', 'UNAVAILABLE', 'NON_WORKING_HOURS'])
          .describe('Availability status of this time slot.')
          .optional(),
        manualApproval: z
          .boolean()
          .describe(
            'Whether manual approval is required to make a reservation in this time slot.'
          )
          .optional()
          .nullable(),
      })
    )
    .min(0)
    .max(100)
    .optional(),
});
